// The client's single To do list (handoff C2 `isTodo`): everything waiting on
// the client, from six sources, overdue first. Shared by the client Home
// ("Needs you"), the To do page and the sidebar pill, so all three agree.
import {
  type VChangeRequest,
  type VMilestone,
  type VRelease,
  type VTask,
  type VTicket,
} from "./data";
import { plural } from "./pages/util";

export type TodoKind = "reply" | "release" | "milestone" | "document" | "action" | "quote";

export interface TodoItem {
  key: string;
  kind: TodoKind;
  label: string; // mono type label: "Reply to us", "Test and approve", …
  icon: string;
  title: string;
  why: string;
  extra: string; // quotes: "£1,140 + VAT · 12 hours"
  meta: string;
  overdue: boolean;
  ticket?: VTicket;
  release?: VRelease;
  milestone?: VMilestone;
  task?: VTask;
  cr?: VChangeRequest;
}

export interface TodoSources {
  tickets: VTicket[];
  releases: VRelease[];
  milestones: VMilestone[];
  tasks: VTask[];
  changeRequests: VChangeRequest[];
  projectName: (pid: string) => string;
}

const ACCOUNT = "Your account";

/** "Thu 8 Oct" (plus the year when it isn't this year). */
export function shortDay(ms: number | null): string {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
  });
}

/** A release waits on the client: on sandbox, needs sign-off, not yet given. */
export const releaseAwaitingSignoff = (r: VRelease) =>
  !r.isFixes && r.status === "On sandbox" && r.requiresSignoff && r.signoff === "pending";

export function buildTodo(s: TodoSources): TodoItem[] {
  const items: TodoItem[] = [];
  const scope = (pid: string | null) => (pid ? s.projectName(pid) : ACCOUNT);

  for (const t of s.tickets) {
    if (t.status !== "Waiting on client") continue;
    items.push({
      key: `t${t.id}`,
      kind: "reply",
      label: "Reply to us",
      icon: "message-square",
      title: t.subject,
      why: "We asked you a question. We've paused the clock until you answer.",
      extra: "",
      meta: s.projectName(t.projectId),
      overdue: false,
      ticket: t,
    });
  }

  for (const r of s.releases) {
    if (!releaseAwaitingSignoff(r)) continue;
    const go = shortDay(r.goLiveAt);
    const by = shortDay(r.approveBy ?? r.goLiveAt);
    items.push({
      key: `r${r.id}`,
      kind: "release",
      label: "Test and approve",
      icon: "rocket",
      title: r.name,
      why: go
        ? `It's ready to try on your sandbox. Approve by ${by} and it goes live ${go}.`
        : "It's ready to try on your sandbox. Approve it and we'll schedule go-live.",
      extra: "",
      meta: r.projectName,
      overdue: false,
      release: r,
    });
  }

  for (const m of s.milestones) {
    if (m.signoff !== "pending" || m.state === "done") continue;
    items.push({
      key: `m${m.id}`,
      kind: "milestone",
      label: "Sign off",
      icon: "flag",
      title: m.name,
      why: m.note,
      extra: "",
      meta: [s.projectName(m.projectId), shortDay(m.dateAt)].filter(Boolean).join(" · "),
      overdue: false,
      milestone: m,
    });
  }

  for (const t of s.tasks) {
    if (t.state !== "Requested") continue;
    const doc = t.kind === "document";
    items.push({
      key: `k${t.id}`,
      kind: doc ? "document" : "action",
      label: doc ? "Send us a file" : "Let us know",
      icon: doc ? "upload" : "square-check",
      title: t.title,
      why: t.detail,
      extra: "",
      meta: [scope(t.projectId), t.dueAt ? `due ${shortDay(t.dueAt)}` : ""]
        .filter(Boolean)
        .join(" · "),
      overdue: t.overdue,
      task: t,
    });
  }

  for (const c of s.changeRequests) {
    if (c.state !== "Awaiting approval") continue;
    const cost = c.cost ? `${c.cost} + VAT` : "";
    const hours = c.estimateHours != null ? plural(c.estimateHours, "hour") : "";
    items.push({
      key: `c${c.id}`,
      kind: "quote",
      label: "Approve a quote",
      icon: "git-branch",
      title: c.title,
      why: c.detail,
      extra: [cost, hours].filter(Boolean).join(" · "),
      meta: [c.ref, s.projectName(c.projectId)].filter(Boolean).join(" · "),
      overdue: false,
      cr: c,
    });
  }

  // Overdue first; otherwise keep source order (stable sort).
  return items.sort((a, b) => Number(b.overdue) - Number(a.overdue));
}

export interface DoneItem {
  key: string;
  title: string;
  line: string;
  at: number;
}

const RECENT_MS = 14 * 86_400_000;

function dayWord(ms: number): string {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(new Date()) - start(new Date(ms))) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return shortDay(ms);
}

/** "Done recently" under the To do list: the client's last five actions. */
export function buildDone(s: TodoSources): DoneItem[] {
  const since = Date.now() - RECENT_MS;
  const out: DoneItem[] = [];
  for (const t of s.tasks) {
    if (t.state === "Submitted") {
      out.push({ key: `k${t.id}`, title: t.title, line: "Sent. We're checking it.", at: t.updatedAt });
    } else if (t.state === "Done" && t.completedAt && t.completedAt >= since) {
      out.push({ key: `k${t.id}`, title: t.title, line: "Done", at: t.completedAt });
    }
  }
  for (const c of s.changeRequests) {
    if (!c.decidedAt || c.decidedAt < since) continue;
    const verb = c.state === "Declined" ? "Declined" : "Approved";
    out.push({ key: `c${c.id}`, title: c.title, line: `${verb} ${dayWord(c.decidedAt)}`, at: c.decidedAt });
  }
  for (const m of s.milestones) {
    if (m.signoff !== "done" || !m.signedOffAt || m.signedOffAt < since) continue;
    out.push({ key: `m${m.id}`, title: m.name, line: "Signed off", at: m.signedOffAt });
  }
  for (const r of s.releases) {
    if (r.signoff !== "done" || !r.signedOffAt || r.signedOffAt < since) continue;
    out.push({ key: `r${r.id}`, title: r.name, line: "Approved for go-live", at: r.signedOffAt });
  }
  return out.sort((a, b) => b.at - a.at).slice(0, 5);
}
